'use client';

/**
 * Visible widget-boot loading state (gap E3).
 *
 * Overlays an app's iframe mount (canvas ContentNode and the standalone
 * `/w` viewer) while the bridge handshake hasn't reached `ready` yet, so a
 * slow-booting app shows its name + a spinner instead of a blank rectangle.
 *
 * Load state comes from the existing per-item log buffer
 * (`lib/workspaces/widget-log-buffer.ts`), which `widget-bridge` already
 * feeds ('ready' on iframe load / SDK handshake, 'failed' on a dead
 * document). The buffer has no subscription API for loadState, so this
 * component POLLS `getWidgetLogs(itemId, 1).loadState` on a short interval —
 * a Map lookup every 250ms, deliberately cheaper than adding a listener
 * registry to a file owned elsewhere. widget-bridge.tsx is untouched.
 *
 * Behaviour:
 *   - visible while loadState === 'loading'
 *   - minimum 300ms on screen once shown (no single-frame flicker)
 *   - after 20s adds a "Still loading..." line AND stops intercepting
 *     pointer events, so a widget that painted but never handshook is still
 *     usable underneath
 *   - after 28s gives up waiting and shows the failure copy with a Reload
 *     action (still click-through, only the button takes events); nothing
 *     else ever sets 'failed' for a widget that simply never reports ready,
 *     and without this the standalone `/w/` page stayed a frosted,
 *     unclickable panel forever
 *   - on a bridge 'failed' (dead document) the panel stays modal and shows
 *     the same failure copy
 *   - root element always renders `data-widget-boot=<state>` so tests and
 *     smoke journeys have a readiness marker even after it hides
 */

import { useEffect, useRef, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { Loader2, RotateCw } from 'lucide-react';
import { getWidgetLogs } from '@/lib/workspaces/widget-log-buffer';
import type { WidgetLoadState } from '@/lib/workspaces/widget-log-buffer';

const POLL_MS = 250;
const MIN_DISPLAY_MS = 300;
const STILL_LOADING_MS = 20_000;
/** Hard stop: past this the overlay reports failure instead of waiting. */
const GIVE_UP_MS = 28_000;

export function WidgetBootOverlay({
  itemId,
  appName,
}: {
  itemId: string;
  appName: string;
}) {
  const t = useT();
  const [loadState, setLoadState] = useState<WidgetLoadState>(
    () => getWidgetLogs(itemId, 1).loadState,
  );
  // True once the 300ms minimum display has elapsed since mount; the overlay
  // only hides when BOTH ready and past the minimum, so a fast boot still
  // paints one steady frame instead of a flash. If the widget is ALREADY
  // ready when we mount (remount over a live iframe), skip the minimum and
  // never show at all.
  const [minShown, setMinShown] = useState(
    () => getWidgetLogs(itemId, 1).loadState === 'ready',
  );
  const [stillLoading, setStillLoading] = useState(false);
  const [gaveUp, setGaveUp] = useState(false);
  const mountedAt = useRef<number>(Date.now());

  useEffect(() => {
    mountedAt.current = Date.now();
    setStillLoading(false);
    setGaveUp(false);
    const minTimer = window.setTimeout(() => setMinShown(true), MIN_DISPLAY_MS);
    const stillTimer = window.setTimeout(() => setStillLoading(true), STILL_LOADING_MS);
    const giveUpTimer = window.setTimeout(() => setGaveUp(true), GIVE_UP_MS);
    const poll = window.setInterval(() => {
      setLoadState(getWidgetLogs(itemId, 1).loadState);
    }, POLL_MS);
    return () => {
      window.clearTimeout(minTimer);
      window.clearTimeout(stillTimer);
      window.clearTimeout(giveUpTimer);
      window.clearInterval(poll);
    };
  }, [itemId]);

  const hidden = loadState === 'ready' && minShown;
  // A hard failure is the bridge telling us the document is dead. Nothing ever
  // sets that for a widget that simply never handshakes, so a long wait counts
  // as failure too, but a SOFTER one: that widget may well have painted.
  const hardFailed = loadState === 'failed';
  const timedOut = gaveUp && loadState !== 'ready';
  const failed = hardFailed || timedOut;
  // Past the "still loading" mark the overlay stops owning the viewport, so an
  // app that painted but never reported ready stays usable underneath and the
  // user is never trapped behind a frosted panel. Only a hard failure keeps
  // the panel modal; the Reload button re-enables its own pointer events.
  const passThrough = !hardFailed && (stillLoading || timedOut);

  return (
    <div
      data-widget-boot={failed ? 'failed' : loadState}
      data-testid="widget-boot-overlay"
      aria-hidden={hidden}
      className={
        hidden
          ? 'hidden'
          : 'absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-background/95 text-center'
            + (passThrough ? ' pointer-events-none' : '')
      }
    >
      {failed ? (
        <>
          <p className="text-sm font-medium text-foreground">{appName}</p>
          <p className="max-w-xs px-4 text-xs text-muted-foreground">
            {t('widgetBoot.didNotLoad')}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="btn-secondary pointer-events-auto flex items-center gap-1.5 text-xs px-3 py-1.5"
          >
            <RotateCw className="h-3.5 w-3.5" />
            Reload
          </button>
        </>
      ) : (
        <>
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">{appName}</p>
          {stillLoading && loadState === 'loading' ? (
            <p className="text-xs text-muted-foreground">{t('widgetBoot.stillLoading')}</p>
          ) : null}
        </>
      )}
    </div>
  );
}
